import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast, TEN_SECONDS, TOAST_ERROR } from 'lib/utils';
import { AskRequest, AskRequestStatus } from '@hone-automation/common';
import { makeRemoteQueueRequestById, makeRemotePostQueueComment } from 'main/factories/usecases/queue';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { UIFlaggedTransaction, UIReportTransaction } from 'types/askcustomer';
import { invalidateQueueRequestsForLocation } from './queueQueryKeys';

type CommentMutationData =
  | FormData
  | {
      comment: string;
      userId?: string;
      locationId?: string;
      activeTransaction: UIFlaggedTransaction | AskRequest | UIReportTransaction;
    };

const getTransactionId = (data: CommentMutationData): string => {
  if (data instanceof FormData) {
    const activeTransaction = JSON.parse(data.get('activeTransaction') as string);
    return activeTransaction?.flagged?.id || activeTransaction?.flaggedCell?.id || activeTransaction?.id;
  }
  const activeTransaction = data.activeTransaction;
  const trxWithFlagged = activeTransaction as UIReportTransaction;
  const trxWithFlaggedCell = activeTransaction as { flaggedCell?: { id: string } };
  const trxWithId = activeTransaction as { id: string };
  return trxWithFlagged?.flagged?.id || trxWithFlaggedCell?.flaggedCell?.id || trxWithId?.id;
};

export const useCreateQueueCommentMutation = (
  status: AskRequestStatus,
  locationId: string,
  setActiveTransaction: (trx: AskRequest | undefined) => void
) => {
  const queryClient = useQueryClient();
  const {
    dispatch,
    state: { requests },
  } = useQueueContext();

  return useMutation<unknown, Error, CommentMutationData>({
    mutationFn: async (data: CommentMutationData) => {
      const trxId = getTransactionId(data);
      const service = makeRemotePostQueueComment(trxId);

      if (data instanceof FormData) {
        const hasAttachments = data.getAll('attachment').length > 0;
        if (hasAttachments) {
          return service.post(data as unknown as any);
        }
        const comment = data.get('comment') as string;
        const userId = data.get('userId') as string;
        const visibility = data.get('visibility') as string;
        const sendEmailRaw = data.get('sendEmail');
        const sendEmail = sendEmailRaw === 'true' ? true : undefined;
        return service.post({ comment, userId, locationId, visibility, sendEmail });
      }

      return service.post({
        comment: data.comment,
        userId: data.userId || '',
        locationId: data.locationId || locationId,
      });
    },
    onSuccess: async (_res, data) => {
      // Invalidate old query key format (used by useQueueRequestsQuery)
      await queryClient.invalidateQueries({ queryKey: ['queueRequests', status, locationId] });
      // Invalidate new query key format (used by useGetQueueRequestsQuery in QueueContext)
      invalidateQueueRequestsForLocation(queryClient, locationId);
      await queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      await queryClient.invalidateQueries({ queryKey: ['flaggedTransactions', locationId] });

      const trxId = getTransactionId(data);

      if (trxId) {
        const getRequestByIdService = makeRemoteQueueRequestById(trxId);
        const updatedTrx = await getRequestByIdService.get();
        setActiveTransaction?.(updatedTrx as AskRequest);
      }
    },
    onError: (error: Error) => {
      console.error('Error creating comment', error);
      showToast(error.message || 'An error has occurred creating message', TOAST_ERROR, TEN_SECONDS);
    },
  });
};
